import types from 'actions/types'
import {combineReducers} from 'redux'
import Immutable from 'seamless-immutable'
import {get, isNil} from 'lodash'

const fetching = (state = Immutable(false), action) => {
  switch (action.type) {
    case types.CONTACTS_DETAILS_CONTACT_LOAD_ATTEMPT:
    case types.CONTACTS_DETAILS_COMPANY_LOAD_ATTEMPT:
      return true
    case types.CONTACTS_DETAILS_CONTACT_LOAD_SUCCESS:
    case types.CONTACTS_DETAILS_COMPANY_LOAD_SUCCESS:
    case types.CONTACTS_DETAILS_CONTACT_LOAD_FAILURE:
    case types.CONTACTS_DETAILS_COMPANY_LOAD_FAILURE:
      return false
    default:
      return state
  }
}

const error = (state = Immutable(null), action) => {
  switch (action.type) {
    case types.CONTACTS_DETAILS_CONTACT_LOAD_ATTEMPT:
    case types.CONTACTS_DETAILS_COMPANY_LOAD_ATTEMPT:
    case types.CONTACTS_DETAILS_CONTACT_LOAD_SUCCESS:
    case types.CONTACTS_DETAILS_COMPANY_LOAD_SUCCESS:
      return null
    case types.CONTACTS_DETAILS_CONTACT_LOAD_FAILURE:
      return state
    case types.CONTACTS_DETAILS_COMPANY_LOAD_FAILURE:
      return action.message || state
    default:
      return state
  }
}

const errorCode = (state = Immutable(null), action) => {
  switch (action.type) {
    case types.CONTACTS_DETAILS_CONTACT_LOAD_ATTEMPT:
    case types.CONTACTS_DETAILS_COMPANY_LOAD_ATTEMPT:
    case types.CONTACTS_DETAILS_CONTACT_LOAD_SUCCESS:
    case types.CONTACTS_DETAILS_COMPANY_LOAD_SUCCESS:
      return null
    case types.CONTACTS_DETAILS_CONTACT_LOAD_FAILURE:
      return state
    case types.CONTACTS_DETAILS_COMPANY_LOAD_FAILURE:
      return action.code || state
    default:
      return state
  }
}

// loaded on loadContact()
export const contact = (state = Immutable(null), action) => {
  switch (action.type) {
    case types.CONTACTS_DETAILS_CONTACT_LOAD_ATTEMPT:
    case types.CONTACTS_DETAILS_COMPANY_LOAD_ATTEMPT:
    case types.CONTACTS_DETAILS_COMPANY_LOAD_SUCCESS:
      return null
    case types.CONTACTS_DETAILS_CONTACT_LOAD_SUCCESS:
      return action.data

    case types.CONTACTS_CONTACT_EDIT_ATTEMPT: {
      if (state && action.contact.id === state.id)
        return action.contact

      return state
    }

    case types.CONTACTS_CONTACT_EDIT_SUCCESS:
      if (!isNil(state) && state.id === action.data.id) {
        return action.data
      }
      return state

    case types.CONTACTS_COMPANY_EDIT_ATTEMPT: {
      if (state && action.company.id === state.companyId)
        return {
          ...state,
          company: action.company,
        }

      return state
    }

    case types.CONTACTS_CONTACT_DELETE_SUCCESS: {
      if (state && action.contactId === state.id)
        return null

      return state
    }

    case types.CONTACTS_CONTACT_DORMANT_SUCCESS:
    case types.CONTACTS_CONTACT_LIVE_SUCCESS:
      return {
        ...state,
        type: action.data.id,
      }

    case types.CONTACTS_UPLOAD_COMPANY_IMAGE_SUCCESS: {
      return isNil(state)
          ? state
          : {
            ...state,
            company: {
              ...state.company,
              companyLogoImage: action.data,
            },
          }
    }

    case types.CONTACTS_REMOVE_COMPANY_IMAGE_SUCCESS: {
      return isNil(state)
          ? state
          : {
            ...state,
            company: {
              ...state.company,
              companyLogoImage: null,
            },
          }
    }

    default:
      return state
  }
}

// loaded on loadContact() and loadCompany()
export const company = (state = Immutable(null), action) => {
  switch (action.type) {
    case types.CONTACTS_DETAILS_COMPANY_LOAD_ATTEMPT:
    case types.CONTACTS_DETAILS_CONTACT_LOAD_ATTEMPT:
      return null
    case types.CONTACTS_DETAILS_COMPANY_LOAD_SUCCESS:
      return action.data
    case types.CONTACTS_DETAILS_CONTACT_LOAD_SUCCESS:
      return action.data.company
    case types.CONTACTS_COMPANY_EDIT_ATTEMPT: {
      if (state && action.company.id === state.id) return action.company
      return state
    }
    case types.CONTACTS_CONTACT_EDIT_SUCCESS:
      const companyContacts = get(state, 'contacts', [])
      return {
        ...state,
        contacts: companyContacts.map(contact => {
          const updatedContact = action.data
          return contact.id === updatedContact.id
              ? updatedContact
              : contact
        }),
      }
    case types.CONTACTS_COMPANY_DELETE_SUCCESS: {
      if (state && action.companyId === state.id) return null
      return state
    }
    case types.CONTACTS_COMPANY_DORMANT_SUCCESS:
      return {
        ...state,
        dormant: true,
      }
    case types.CONTACTS_COMPANY_LIVE_SUCCESS:
      return {
        ...state,
        dormant: false,
      }

    case types.CONTACTS_UPLOAD_COMPANY_IMAGE_SUCCESS: {
      return isNil(state)
          ? state
          : {
            ...state,
            companyLogoImage: action.data,
          }
    }

    case types.CONTACTS_REMOVE_COMPANY_IMAGE_SUCCESS: {
      return isNil(state)
          ? state
          : {
            ...state,
            companyLogoImage: null,
          }
    }

    default:
      return state
  }
}

export const sortedContacts = (contacts) => {
  const ret = [...contacts]
  ret.sort((a, b) => {
    const nameA = `${a.forename} ${a.surname}`
    const nameB = `${b.forename} ${b.surname}`
    if (nameA > nameB) return 1
    if (nameA < nameB) return -1
    return 0
  })
  return ret
}

// loaded on loadContact() and loadCompany()
const companyContacts = (state = Immutable([]), action) => {
  switch (action.type) {
    case types.CONTACTS_DETAILS_COMPANY_LOAD_ATTEMPT:
    case types.CONTACTS_DETAILS_CONTACT_LOAD_ATTEMPT:
      return null
    case types.CONTACTS_DETAILS_COMPANY_LOAD_SUCCESS:
      return sortedContacts(action.data.contacts || [])
    case types.CONTACTS_DETAILS_CONTACT_LOAD_SUCCESS:
      return sortedContacts(action.data.company.contacts || [])
    case types.CONTACTS_CONTACT_EDIT_ATTEMPT: {
      if (state) {
        return state.map(c => {
          if (c.id === action.contact.id) return action.contact
          if (action.contact.type === 1 && c.type === 1) return {...c, type: 0} // demote previous main to general
          if (action.contact.newMainContactId && c.id === action.contact.newMainContactId) return {...c, type: 1} // promote to main if replacing main
          return c
        })
      }
      return state
    }
    case types.CONTACTS_CONTACT_EDIT_SUCCESS:
      return state.map(contact => {
        const updatedContact = action.data
        return contact.id === updatedContact.id
            ? updatedContact
            : contact
      })
    case types.CONTACTS_CONTACT_DELETE_SUCCESS: {
      if (state) {
        let ret = state.filter(c => c.id !== action.contactId)  // remove deleted contact
        if (action.replaceId) {
          ret = ret.map(c => {
            if (c.id === action.replaceId) return {...c, type: 1} // promote to main if replacing main
            return c
          })
        }
        return ret
      }
      return state
    }
    case types.CONTACTS_CONTACT_DORMANT_SUCCESS:
    case types.CONTACTS_CONTACT_LIVE_SUCCESS:
      if (state) {
        return state.map(c => {
          if (c.id === action.contactId) return {...c, type: action.data.id}
          if (action.replaceId && c.id === action.replaceId) return {...c, type: 1} // promote to main if replacing main
          return c
        })
      }
      break
    default:
      return state
  }
}

const demoteMainLocation = (locations, mainLocationId) => {
  return locations.map(loc => {
    if (loc.id === mainLocationId) return loc
    if (loc.type.id === -1) return {...loc, type: {id: 0}} // demote to general
    return loc
  })
}
// loaded on loadContact() and loadCompany()
const locations = (state = Immutable([]), action) => {
  switch (action.type) {
    case types.CONTACTS_DETAILS_COMPANY_LOAD_ATTEMPT:
    case types.CONTACTS_DETAILS_CONTACT_LOAD_ATTEMPT:
      return []
    case types.CONTACTS_DETAILS_COMPANY_LOAD_SUCCESS:
      return action.data.locations || []
    case types.CONTACTS_DETAILS_CONTACT_LOAD_SUCCESS:
      return get(action.data, 'company.locations')

    case types.CONTACTS_LOCATION_ADD_SUCCESS: {
      let ret = [
        ...state,
        action.data,
      ]
      if (action.data.type.id === -1) ret = demoteMainLocation(ret, action.data.id)
      return ret
    }

    case types.CONTACTS_LOCATION_EDIT_SUCCESS: {
      const removed = state.filter(location => location.id !== action.locationId)
      let ret = [
        action.location,
        ...removed,
      ]
      if (action.location.type.id === -1) ret = demoteMainLocation(ret, action.locationId)
      return ret
    }

    case types.CONTACTS_LOCATION_DELETE_SUCCESS:
      return state.filter(location => location.id !== action.data)
    default:
      return state
  }
}

// loaded on loadContact() and loadCompany()
const feedback = (state = null, action) => {
  switch (action.type) {
    case types.CONTACTS_DETAILS_COMPANY_LOAD_ATTEMPT:
    case types.CONTACTS_DETAILS_CONTACT_LOAD_ATTEMPT:
      return null
    case types.CONTACTS_DETAILS_COMPANY_LOAD_SUCCESS:
    case types.CONTACTS_DETAILS_CONTACT_LOAD_SUCCESS:
      return action.data.feedback || []
    default:
      return state
  }
}

export default combineReducers({
  fetching,
  error,
  errorCode,
  contact,
  company,
  companyContacts,
  locations,
  feedback,
})
